<template>
  <div class="slider-captcha-container">
    <div class="slider-track" :class="{ 'slider-success': passed, 'slider-active': dragging }">
      <div
        class="slider-thumb"
        :style="{ left: thumbLeft + 'px', background: passed ? '#52c41a' : (dragging ? 'linear-gradient(90deg, #52c41a 0%, #67c23a 100%)' : '#e0e0e0') }"
        @mousedown="startDrag"
        @touchstart="startDrag"
        :class="{ 'slider-thumb-success': passed }"
      >
        <svg v-if="!passed" width="22" height="22" viewBox="0 0 22 22" style="display:block;margin:auto;" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="10" width="12" height="2" rx="1" fill="#888"/>
          <polygon points="14,6 20,11 14,16" fill="#888"/>
        </svg>
        <i v-else class="el-icon-check" style="color: #fff; font-size: 20px;"></i>
      </div>
      <span class="slider-text" :class="{ 'slider-text-success': passed }">
        {{ passed ? '验证通过' : '按住滑块，拖动到最右边' }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch, defineExpose } from 'vue'
const props = defineProps({
  modelValue: Boolean // 控制验证通过状态
})
const emit = defineEmits(['update:modelValue', 'success'])
const passed = ref(props.modelValue || false)
const dragging = ref(false)
const thumbLeft = ref(0)
const sliderWidth = ref(0)
const thumbWidth = 40
const thumbMaxLeft = ref(0)

watch(() => props.modelValue, val => passed.value = val)
watch(passed, val => emit('update:modelValue', val))

// 暴露 reset 方法，外部可调用让滑块回到初始状态
function reset() {
  passed.value = false
  dragging.value = false
  thumbLeft.value = 0
}
defineExpose({ reset })

const startDrag = (e:any|null) => {
  if (passed.value) return
  dragging.value = true
  const thumb = e.target
  thumb.classList.add('slider-thumb-dragging')
  // 获取滑块中心点与鼠标的初始偏移，保证拖动时滑块中心和鼠标始终同步
  const sliderRect = thumb.parentNode.getBoundingClientRect()
  const thumbRect = thumb.getBoundingClientRect()
  const startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
  // 鼠标点在滑块内的偏移
  const offsetX = startX - thumbRect.left


  // requestAnimationFrame 优化拖动
  let rafId: any | null = null
  let lastClientX = startX
  let moving = false

  const updateThumb = () => {
    if (!moving) return
    let newLeft = lastClientX - sliderRect.left - offsetX
    newLeft = Math.min(Math.max(newLeft, 0), thumbMaxLeft.value)
    thumbLeft.value = newLeft
    rafId = requestAnimationFrame(updateThumb)
  }

  const onMove = (moveEvent:any | null) => {
    lastClientX = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientX : moveEvent.clientX
    if (!moving) {
      moving = true
      rafId = requestAnimationFrame(updateThumb)
    }
  }


  const onUp = () => {
    dragging.value = false
    thumb.classList.remove('slider-thumb-dragging')
    moving = false
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    if (thumbLeft.value >= thumbMaxLeft.value - 2) {
      passed.value = true
      thumbLeft.value = thumbMaxLeft.value
      emit('success')
    } else {
      // 回弹动画更丝滑
      const animateBack = () => {
        if (thumbLeft.value > 0) {
          thumbLeft.value = Math.max(0, thumbLeft.value - Math.max(2, thumbLeft.value * 0.2))
          requestAnimationFrame(animateBack)
        } else {
          thumbLeft.value = 0
        }
      }
      animateBack()
    }
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
    window.removeEventListener('touchmove', onMove)
    window.removeEventListener('touchend', onUp)
  }

  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
  window.addEventListener('touchmove', onMove)
  window.addEventListener('touchend', onUp)
}

onMounted(() => {
  nextTick(() => {
    const track = document.querySelector('.slider-track')
    if (track) {
      sliderWidth.value = (track as HTMLElement).offsetWidth
      thumbMaxLeft.value = sliderWidth.value - thumbWidth
    }
    thumbLeft.value = 0
    passed.value = false
  })
})
</script>

<style scoped>
.slider-captcha-container {
  width: 100%;
  margin: 0 auto 12px auto;
  user-select: none;
}
.slider-track {
  position: relative;
  width: 100%;
  height: 40px;
  background: #f5f5f5;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  overflow: hidden;
  transition: border-color 0.3s, background 0.3s;
}
.slider-track.slider-success {
  border-color: #52c41a;
  background: #52c41a;
}
.slider-track.slider-active:not(.slider-success) {
  background: linear-gradient(90deg, #b7eb8f 0%, #f5f5f5 100%);
}
.slider-thumb {
  position: absolute;
  top: 0;
  width: 40px;
  height: 40px;
  background: #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(36,198,220,0.08);
  color: #666;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;
  user-select: none;
  transform: translateX(0);
}
.slider-thumb svg {
  transition: transform 0.3s ease;
}
.slider-thumb.slider-thumb-dragging svg {
  transform: rotate(180deg);
}
.slider-thumb-success {
  background: #52c41a !important;
  color: #fff;
}
.slider-text {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 40px;
  line-height: 40px;
  text-align: center;
  color: #888;
  font-size: 15px;
  z-index: 1;
  pointer-events: none;
  transition: color 0.3s;
}
.slider-text-success {
  color: #fff;
  font-weight: bold;
}
</style>
